import React, { useState, useEffect, useCallback } from "react"
import { motion } from "framer-motion"

interface SlideData {
  id: string
  title: string
  subTitle: string
  image: string
  href?: string
}

const slidesData: SlideData[] = [
  {
    id: "1",
    title: "",
    subTitle: "",
    image: "/web-ttmi/homepage/slides/slide-5.png",
  },
  {
    id: "2",
    title: "",
    subTitle: "",
    image: "/web-ttmi/homepage/slides/slide-6.jpg",
  },
  {
    id: "3",
    title: "Một khoảng thở tĩnh lặng...",
    subTitle: "Thien Truc Lounge",
    image: "/web-ttmi/homepage/slides/slide-1.png",
    href: "/dia-diem",
  },
  {
    id: "4",
    title: "Một khoảng thở tĩnh lặng...",
    subTitle: "Thien Truc Lounge",
    image: "/web-ttmi/homepage/slides/slide-2.png",
    href: "/dat-ban",
  },
  {
    id: "5",
    title: "Một khoảng thở tĩnh lặng...",
    subTitle: "Thien Truc Lounge",
    image: "/web-ttmi/homepage/slides/slide-3.png",
    href: "/dat-ban",
  },
  {
    id: "6",
    title: "Một khoảng thở tĩnh lặng...",
    subTitle: "Thien Truc Lounge",
    image: "/web-ttmi/homepage/slides/slide-4.png",
    href: "/dat-ban",
  },
]

const Slides: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isAutoPlaying, setIsAutoPlaying] = useState(true)
  const [isPaused, setIsPaused] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [currentX, setCurrentX] = useState(0)
  const [dragOffset, setDragOffset] = useState(0)

  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % slidesData.length)
  }, [])

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index)
  }, [])

  useEffect(() => {
    if (!isAutoPlaying || isPaused) return

    const interval = setInterval(() => {
      nextSlide()
    }, 5000)

    return () => clearInterval(interval)
  }, [isAutoPlaying, isPaused, nextSlide])

  const handleMouseEnter = () => {
    setIsPaused(true)
  }

  const handleMouseLeave = () => {
    setIsPaused(false)
  }

  const handleDragStart = (clientX: number) => {
    setIsDragging(true)
    setStartX(clientX)
    setCurrentX(clientX)
    setIsPaused(true)
  }

  const handleDragMove = (clientX: number) => {
    if (!isDragging) return

    setCurrentX(clientX)
    const diff = clientX - startX
    setDragOffset(diff)
  }

  const handleDragEnd = () => {
    if (!isDragging) return

    const diff = currentX - startX
    const threshold = 50

    if (Math.abs(diff) > threshold) {
      if (diff > 0 && currentSlide > 0) {
        setCurrentSlide(prev => prev - 1)
      } else if (diff < 0 && currentSlide < slidesData.length - 1) {
        setCurrentSlide(prev => prev + 1)
      }
    }

    setIsDragging(false)
    setDragOffset(0)
    setIsPaused(false)
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    handleDragStart(e.clientX)
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    handleDragMove(e.clientX)
  }

  const handleMouseUp = () => {
    handleDragEnd()
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    handleDragStart(e.touches[0].clientX)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    handleDragMove(e.touches[0].clientX)
  }

  const handleTouchEnd = () => {
    handleDragEnd()
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      viewport={{ once: true }}
      className="relative w-full max-w-[1500px] mx-auto mt-20 px-4 sm:px-6 md:px-8 lg:px-0 select-none cursor-grab active:cursor-grabbing"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      role="region"
      aria-roledescription="carousel"
      aria-label="Carousel"
    >
      {/* Swiper Wrapper */}
      <div className="overflow-hidden">
        <div
          className={`flex ${
            isDragging
              ? "transition-none"
              : "transition-transform duration-500 ease-in-out"
          }`}
          style={{
            transform: `translateX(calc(-${
              currentSlide * 100
            }% + ${dragOffset}px))`,
          }}
          aria-live="off"
        >
          {slidesData.map((slide, index) => (
            <div
              key={slide.id}
              className="relative w-full flex-shrink-0"
              role="group"
              aria-roledescription="slide"
              aria-label={`${index + 1} / ${slidesData.length}`}
              data-swiper-slide-index={index}
              aria-hidden={currentSlide !== index}
            >
              <div className="relative flex">
                <div className="w-full">
                  <div className="relative w-full aspect-[9/4] overflow-hidden">
                    <img
                      src={slide.image}
                      alt=""
                      className="w-full h-full object-fill"
                      loading={index === 0 ? "eager" : "lazy"}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent"></div>
                  </div>

                  <div className="absolute inset-0 flex flex-col justify-end items-start p-4 sm:p-6 md:p-12 lg:p-16">
                    <motion.div
                      className="mb-2"
                      initial={{ opacity: 0, y: 20 }}
                      animate={
                        currentSlide === index
                          ? { opacity: 1, y: 0 }
                          : { opacity: 0, y: 20 }
                      }
                      transition={{
                        duration: 0.6,
                        delay: 0.3,
                        ease: "easeOut",
                      }}
                    >
                      <motion.div
                        className="text-white text-xs sm:text-base md:text-lg font-light mb-1 sm:mb-2"
                        initial={{ opacity: 0 }}
                        animate={
                          currentSlide === index
                            ? { opacity: 1 }
                            : { opacity: 0 }
                        }
                        transition={{ duration: 0.5, delay: 0.4 }}
                      >
                        {slide.subTitle}
                      </motion.div>
                      <motion.h2
                        className="text-white text-sm sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-normal"
                        initial={{ opacity: 0 }}
                        animate={
                          currentSlide === index
                            ? { opacity: 1 }
                            : { opacity: 0 }
                        }
                        transition={{ duration: 0.5, delay: 0.5 }}
                      >
                        {slide.title}
                      </motion.h2>
                    </motion.div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pagination Bullets */}
      <div className="absolute bottom-6 right-6 sm:bottom-8 sm:right-8 md:bottom-12 md:right-12 lg:bottom-16 lg:right-16">
        <div className="flex space-x-2">
          {slidesData.map((_, index) => (
            <button
              key={index}
              className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full transition-colors duration-300 ${
                currentSlide === index
                  ? "bg-white"
                  : "bg-white/50 hover:bg-white/75"
              }`}
              onClick={() => goToSlide(index)}
              role="button"
              data-bullet-index={index}
              aria-label={`Đi đến slide ${index + 1}`}
              tabIndex={currentSlide === index ? 0 : -1}
              aria-current={currentSlide === index ? "true" : undefined}
            />
          ))}
        </div>
      </div>

      {/* Screen Reader Notification */}
      <span className="sr-only" aria-live="assertive" aria-atomic="true">
        Slide {currentSlide + 1} of {slidesData.length}
      </span>
    </motion.div>
  )
}

export default Slides
